// AI Turnover Scheduler — Server engine adapter (Sprint 5).
//
// Wraps the supply/demand RPCs defined in
// `supabase/migrations/20260425113000_scheduler_engine_v1.sql`. Each wrapper:
//
//   1. Parses the raw `unknown` payload into the typed shape declared in
//      `./types.ts` (mirrors glossary section 8).
//   2. Returns a discriminated `SchedulerCallResult` that carries the data
//      source (`server` vs `client_fallback`) and a typed error if any.
//
// The Sprint 5 UI layer builds React hooks on top of these adapters
// (`src/hooks/scheduler/*`). This module stays React-free so it can be reused
// by tests, CLI scripts and the upcoming KPI board (Sprint 8).

import { supabaseUntyped } from "@/integrations/supabase/untyped";
import {
  RULES_VERSION,
  type RiskCounts,
  type RiskLabel,
  type RiskReasonCode,
  type RiskReasonScope,
  type ServerBatchRowFlags,
  type ServerDayFlag,
  type ServerRiskReason,
  type ServerSchedulerSummary,
  type ServerVendorCapacityDay,
  type ServerVendorLoadDay,
} from "./types";

// ---------------------------------------------------------------------------
// Public result shape
// ---------------------------------------------------------------------------

export type SchedulerSource = "server" | "client_fallback";

export interface SchedulerError {
  /** PostgREST / network code when available. */
  code?: string;
  message: string;
  /** Marks errors that should silently degrade to fallback (404, RPC missing). */
  recoverable: boolean;
}

export interface SchedulerCallResult<T> {
  data: T | null;
  source: SchedulerSource | null;
  error: SchedulerError | null;
}

/**
 * Read `VITE_SCHEDULER_SERVER_DISABLED=1` to force the client fallback. Used
 * as a kill-switch during Sprint 5 rollout: the safety-net engine in
 * `./rules.ts` keeps every surface working without redeploying the SQL motor.
 */
export function isServerEngineDisabled(): boolean {
  try {
    return import.meta.env.VITE_SCHEDULER_SERVER_DISABLED === "1";
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Parsers — defensive against shape drift from PostgREST
// ---------------------------------------------------------------------------

const VALID_REASON_CODES = new Set<RiskReasonCode>([
  "URGENT",
  "TIGHT_WINDOW",
  "UNSCHEDULED",
  "SAME_DAY_TURNOVER",
  "OVER_CAPACITY_DAY",
  "NEAR_CAPACITY_DAY",
  "CLOSED_DAY_WITH_LOAD",
  "SAME_DAY_TURNOVER_CLUSTER",
]);

function asObject(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function toInt(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    if (Number.isFinite(n)) return Math.trunc(n);
  }
  return fallback;
}

function toFloat(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

function toBool(value: unknown): boolean {
  return value === true;
}

function toRiskLabel(value: unknown): RiskLabel {
  if (value === "critico" || value === "attenzione" || value === "stabile") {
    return value;
  }
  return "stabile";
}

function parseCounts(value: unknown): RiskCounts {
  const obj = asObject(value) ?? {};
  return {
    total: toInt(obj.total),
    tight: toInt(obj.tight),
    urgent: toInt(obj.urgent),
    unscheduled: toInt(obj.unscheduled),
    same_day: toInt(obj.same_day),
  };
}

function parseRiskReasons(value: unknown): ServerRiskReason[] {
  if (!Array.isArray(value)) return [];
  const out: ServerRiskReason[] = [];
  for (const raw of value) {
    const obj = asObject(raw);
    if (!obj) continue;
    const code = obj.code as RiskReasonCode;
    if (!VALID_REASON_CODES.has(code)) continue;
    const scope = obj.scope === "days" || obj.scope === "rows" ? (obj.scope as RiskReasonScope) : "rows";
    out.push({
      code,
      count: toInt(obj.count),
      scope,
    });
  }
  return out;
}

function parseDayFlags(value: unknown): ServerDayFlag[] {
  if (!Array.isArray(value)) return [];
  const out: ServerDayFlag[] = [];
  for (const raw of value) {
    const obj = asObject(raw);
    if (!obj) continue;
    const date = typeof obj.date === "string" ? obj.date : null;
    if (!date) continue;
    out.push({
      date,
      over_capacity_day: toBool(obj.over_capacity_day),
      near_capacity_day: toBool(obj.near_capacity_day),
      closed_day_with_load: toBool(obj.closed_day_with_load),
      same_day_turnover_cluster: toBool(obj.same_day_turnover_cluster),
      vendor_load: toInt(obj.vendor_load),
      capacity_published: toInt(obj.capacity_published),
    });
  }
  return out;
}

/**
 * Parse the standard summary jsonb returned by every `get_*_summary` RPC.
 * Returns `null` if the payload is too malformed to be trusted (callers
 * should fall back to the client engine in that case).
 */
export function parseServerSummary(payload: unknown): ServerSchedulerSummary | null {
  const obj = asObject(payload);
  if (!obj) return null;
  const counts = parseCounts(obj.counts);
  const risk_label = toRiskLabel(obj.risk_label);
  const suggested_action = typeof obj.suggested_action === "string" ? obj.suggested_action : "";
  const rules_version = typeof obj.rules_version === "string" ? obj.rules_version : RULES_VERSION;
  const computed_at = typeof obj.computed_at === "string" ? obj.computed_at : new Date().toISOString();
  const confidence_score = toFloat(obj.confidence_score);
  return {
    risk_label,
    risk_reasons: parseRiskReasons(obj.risk_reasons),
    suggested_action,
    counts,
    day_flags: parseDayFlags(obj.day_flags),
    confidence_score: Math.max(0, Math.min(1, confidence_score)),
    rules_version,
    computed_at,
  };
}

function parseVendorLoadRow(raw: unknown): ServerVendorLoadDay | null {
  const obj = asObject(raw);
  if (!obj) return null;
  const day = typeof obj.day === "string" ? obj.day : null;
  if (!day) return null;
  return {
    day,
    vendor_load: toInt(obj.vendor_load),
    capacity_published: toInt(obj.capacity_published),
    blocked: toBool(obj.blocked),
    same_day_turnover_count: toInt(obj.same_day_turnover_count),
    over_capacity_day: toBool(obj.over_capacity_day),
    near_capacity_day: toBool(obj.near_capacity_day),
    closed_day_with_load: toBool(obj.closed_day_with_load),
    same_day_turnover_cluster: toBool(obj.same_day_turnover_cluster),
  };
}

function parseVendorCapacityRow(raw: unknown): ServerVendorCapacityDay | null {
  const obj = asObject(raw);
  if (!obj) return null;
  const day = typeof obj.day === "string" ? obj.day : null;
  if (!day) return null;
  const slotsRaw = Array.isArray(obj.slots) ? obj.slots : [];
  const slots = slotsRaw
    .map((s) => {
      const slotObj = asObject(s);
      if (!slotObj) return null;
      const slot = typeof slotObj.slot === "string" ? slotObj.slot : null;
      if (!slot) return null;
      return {
        slot,
        capacity: toInt(slotObj.capacity),
        blocked: toBool(slotObj.blocked),
      };
    })
    .filter((s): s is { slot: string; capacity: number; blocked: boolean } => s !== null);
  return {
    day,
    capacity_published: toInt(obj.capacity_published),
    blocked: toBool(obj.blocked),
    slots,
  };
}

function parseBatchRow(raw: unknown): ServerBatchRowFlags | null {
  const obj = asObject(raw);
  if (!obj) return null;
  const booking_id = typeof obj.booking_id === "string" ? obj.booking_id : null;
  if (!booking_id) return null;
  return {
    booking_id,
    effective_date: typeof obj.effective_date === "string" ? obj.effective_date : null,
    tight_window: toBool(obj.tight_window),
    urgent: toBool(obj.urgent),
    unscheduled: toBool(obj.unscheduled),
    same_day_turnover: toBool(obj.same_day_turnover),
  };
}

// ---------------------------------------------------------------------------
// Error classification
// ---------------------------------------------------------------------------

/**
 * PostgREST error codes / messages we treat as RECOVERABLE — the UI silently
 * downgrades to the client fallback. Anything else surfaces as an error.
 */
const RECOVERABLE_CODES = new Set<string>([
  "PGRST202", // function not found in schema cache (motor not deployed yet)
  "404",
]);

function classifyError(err: unknown): SchedulerError {
  const obj = asObject(err);
  const code = obj && typeof obj.code === "string" ? obj.code : undefined;
  const status = obj && typeof obj.status === "number" ? String(obj.status) : undefined;
  const message =
    (obj && typeof obj.message === "string" ? obj.message : null) ??
    (err instanceof Error ? err.message : "Errore sconosciuto dal server scheduler.");
  const recoverable = !!(
    (code && RECOVERABLE_CODES.has(code)) ||
    (status && RECOVERABLE_CODES.has(status))
  );
  return { code: code ?? status, message, recoverable };
}

// ---------------------------------------------------------------------------
// RPC wrappers
// ---------------------------------------------------------------------------

// The generated `Database` type does not yet expose the Sprint-4 RPCs
// (`get_*_summary`, etc.). We use the documented escape hatch from
// `@/integrations/supabase/untyped` and re-add type safety at the parser
// boundary above (`parseServerSummary`, `parseVendorLoadRow`, ...).
const sb = () => supabaseUntyped;

export async function fetchBatchSchedulerSummary(params: {
  companyId: string;
  batchId: string;
}): Promise<SchedulerCallResult<ServerSchedulerSummary>> {
  if (isServerEngineDisabled()) {
    return { data: null, source: null, error: { message: "scheduler_disabled", recoverable: true } };
  }
  try {
    const { data, error } = await sb().rpc("get_batch_scheduler_summary", {
      p_company_id: params.companyId,
      p_batch_id: params.batchId,
    });
    if (error) return { data: null, source: null, error: classifyError(error) };
    const parsed = parseServerSummary(data);
    if (!parsed) {
      return {
        data: null,
        source: null,
        error: { message: "Risposta scheduler non valida.", recoverable: true },
      };
    }
    return { data: parsed, source: "server", error: null };
  } catch (err) {
    return { data: null, source: null, error: classifyError(err) };
  }
}

export async function fetchVendorRiskSummary(params: {
  companyId: string;
  startDate: string;
  endDate: string;
}): Promise<SchedulerCallResult<ServerSchedulerSummary>> {
  if (isServerEngineDisabled()) {
    return { data: null, source: null, error: { message: "scheduler_disabled", recoverable: true } };
  }
  try {
    const { data, error } = await sb().rpc("get_vendor_risk_summary", {
      p_company_id: params.companyId,
      p_start_date: params.startDate,
      p_end_date: params.endDate,
    });
    if (error) return { data: null, source: null, error: classifyError(error) };
    const parsed = parseServerSummary(data);
    if (!parsed) {
      return {
        data: null,
        source: null,
        error: { message: "Risposta scheduler non valida.", recoverable: true },
      };
    }
    return { data: parsed, source: "server", error: null };
  } catch (err) {
    return { data: null, source: null, error: classifyError(err) };
  }
}

export async function fetchVendorLoadSnapshot(params: {
  companyId: string;
  startDate: string;
  endDate: string;
}): Promise<SchedulerCallResult<ServerVendorLoadDay[]>> {
  if (isServerEngineDisabled()) {
    return { data: null, source: null, error: { message: "scheduler_disabled", recoverable: true } };
  }
  try {
    const { data, error } = await sb().rpc("get_vendor_load_snapshot", {
      p_company_id: params.companyId,
      p_start_date: params.startDate,
      p_end_date: params.endDate,
    });
    if (error) return { data: null, source: null, error: classifyError(error) };
    const rows = Array.isArray(data) ? data.map(parseVendorLoadRow).filter((r): r is ServerVendorLoadDay => r !== null) : [];
    return { data: rows, source: "server", error: null };
  } catch (err) {
    return { data: null, source: null, error: classifyError(err) };
  }
}

export async function fetchVendorCapacitySnapshot(params: {
  companyId: string;
  startDate: string;
  endDate: string;
}): Promise<SchedulerCallResult<ServerVendorCapacityDay[]>> {
  if (isServerEngineDisabled()) {
    return { data: null, source: null, error: { message: "scheduler_disabled", recoverable: true } };
  }
  try {
    const { data, error } = await sb().rpc("get_vendor_capacity_snapshot", {
      p_company_id: params.companyId,
      p_start_date: params.startDate,
      p_end_date: params.endDate,
    });
    if (error) return { data: null, source: null, error: classifyError(error) };
    const rows = Array.isArray(data)
      ? data.map(parseVendorCapacityRow).filter((r): r is ServerVendorCapacityDay => r !== null)
      : [];
    return { data: rows, source: "server", error: null };
  } catch (err) {
    return { data: null, source: null, error: classifyError(err) };
  }
}

export async function fetchBatchSchedulerRows(params: {
  companyId: string;
  batchId: string;
}): Promise<SchedulerCallResult<ServerBatchRowFlags[]>> {
  if (isServerEngineDisabled()) {
    return { data: null, source: null, error: { message: "scheduler_disabled", recoverable: true } };
  }
  try {
    const { data, error } = await sb().rpc("get_batch_scheduler_rows", {
      p_company_id: params.companyId,
      p_batch_id: params.batchId,
    });
    if (error) return { data: null, source: null, error: classifyError(error) };
    const rows = Array.isArray(data) ? data.map(parseBatchRow).filter((r): r is ServerBatchRowFlags => r !== null) : [];
    return { data: rows, source: "server", error: null };
  } catch (err) {
    return { data: null, source: null, error: classifyError(err) };
  }
}

// ---------------------------------------------------------------------------
// Demand-side wrappers (Sprint 6)
// ---------------------------------------------------------------------------
//
// PRIVACY: these only ever call `get_demand_*` RPCs, which by contract never
// read the private supply-side external-bookings table and always return
// `day_flags: []` (the unified vendor load is private to supply). Glossary 8.3.

export async function fetchDemandPortfolioSummary(params: {
  profileId: string;
  startDate: string;
  endDate: string;
}): Promise<SchedulerCallResult<ServerSchedulerSummary>> {
  if (isServerEngineDisabled()) {
    return { data: null, source: null, error: { message: "scheduler_disabled", recoverable: true } };
  }
  try {
    const { data, error } = await sb().rpc("get_demand_portfolio_summary", {
      p_profile_id: params.profileId,
      p_start_date: params.startDate,
      p_end_date: params.endDate,
    });
    if (error) return { data: null, source: null, error: classifyError(error) };
    const parsed = parseServerSummary(data);
    if (!parsed) {
      return {
        data: null,
        source: null,
        error: { message: "Risposta scheduler non valida.", recoverable: true },
      };
    }
    return { data: parsed, source: "server", error: null };
  } catch (err) {
    return { data: null, source: null, error: classifyError(err) };
  }
}

export async function fetchDemandBatchStatus(params: {
  batchId: string;
}): Promise<SchedulerCallResult<ServerSchedulerSummary>> {
  if (isServerEngineDisabled()) {
    return { data: null, source: null, error: { message: "scheduler_disabled", recoverable: true } };
  }
  try {
    const { data, error } = await sb().rpc("get_demand_batch_status", {
      p_batch_id: params.batchId,
    });
    if (error) return { data: null, source: null, error: classifyError(error) };
    const parsed = parseServerSummary(data);
    if (!parsed) {
      return {
        data: null,
        source: null,
        error: { message: "Risposta scheduler non valida.", recoverable: true },
      };
    }
    return { data: parsed, source: "server", error: null };
  } catch (err) {
    return { data: null, source: null, error: classifyError(err) };
  }
}

// ---------------------------------------------------------------------------
// Fallback bridge — let the client v1 engine emit a server-shaped summary.
// ---------------------------------------------------------------------------
//
// The hooks call this when the server engine is unreachable AND the consumer
// supplied row/day inputs. Keeps the UI rendering identical regardless of
// source, so we can flip the kill-switch with no visual delta.

import type { DayLoadFlags, RowRiskFlags } from "./types";
import { aggregateRiskCounts, computeRiskLabel } from "./rules";
import { getSuggestedAction } from "./microcopy";

export interface ClientFallbackInput {
  rowFlags: RowRiskFlags[];
  dayFlags?: Array<{ date: string } & DayLoadFlags & { vendor_load?: number; capacity_published?: number }>;
}

/**
 * Same shape and confidence formula as `_scheduler_compute_summary_internal`
 * in the SQL motor. Used by the hooks when the server is unreachable.
 */
export function buildClientFallbackSummary(input: ClientFallbackInput): ServerSchedulerSummary {
  const counts = aggregateRiskCounts(input.rowFlags);
  const dayFlagsArr = input.dayFlags ?? [];
  const risk_label = computeRiskLabel(counts, dayFlagsArr);
  const suggested_action = getSuggestedAction(counts, dayFlagsArr);

  const overCapacityDays = dayFlagsArr.filter((d) => d.over_capacity_day).length;
  const nearCapacityDays = dayFlagsArr.filter((d) => d.near_capacity_day).length;
  const closedWithLoad = dayFlagsArr.filter((d) => d.closed_day_with_load).length;
  const sameDayClusters = dayFlagsArr.filter((d) => d.same_day_turnover_cluster).length;

  // Mirrors _scheduler_compute_summary_internal: confidence = 1 - unscheduled
  // ratio penalty, capped at 0.5. We don't penalize for missing capacity in
  // the fallback because the client doesn't always know the full vendor scope.
  let confidence = 1;
  if (counts.total === 0) {
    confidence = 0;
  } else {
    confidence -= Math.min(0.5, (counts.unscheduled / counts.total) * 0.5);
  }
  confidence = Math.max(0, Math.min(1, confidence));

  const reasons: ServerRiskReason[] = [];
  if (counts.urgent > 0) reasons.push({ code: "URGENT", count: counts.urgent, scope: "rows" });
  if (overCapacityDays > 0) reasons.push({ code: "OVER_CAPACITY_DAY", count: overCapacityDays, scope: "days" });
  if (closedWithLoad > 0) reasons.push({ code: "CLOSED_DAY_WITH_LOAD", count: closedWithLoad, scope: "days" });
  if (counts.tight > 0) reasons.push({ code: "TIGHT_WINDOW", count: counts.tight, scope: "rows" });
  if (counts.unscheduled > 0) reasons.push({ code: "UNSCHEDULED", count: counts.unscheduled, scope: "rows" });
  if (nearCapacityDays > 0) reasons.push({ code: "NEAR_CAPACITY_DAY", count: nearCapacityDays, scope: "days" });
  if (sameDayClusters > 0) reasons.push({ code: "SAME_DAY_TURNOVER_CLUSTER", count: sameDayClusters, scope: "days" });
  if (counts.same_day > 0) reasons.push({ code: "SAME_DAY_TURNOVER", count: counts.same_day, scope: "rows" });

  const day_flags: ServerDayFlag[] = dayFlagsArr.map((d) => ({
    date: d.date,
    over_capacity_day: d.over_capacity_day,
    near_capacity_day: d.near_capacity_day,
    closed_day_with_load: d.closed_day_with_load,
    same_day_turnover_cluster: d.same_day_turnover_cluster,
    vendor_load: typeof d.vendor_load === "number" ? d.vendor_load : 0,
    capacity_published: typeof d.capacity_published === "number" ? d.capacity_published : 0,
  }));

  return {
    risk_label,
    risk_reasons: reasons,
    suggested_action,
    counts,
    day_flags,
    confidence_score: Math.round(confidence * 1000) / 1000,
    rules_version: RULES_VERSION,
    computed_at: new Date().toISOString(),
  };
}
